import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getWhatsAppMenuConfigAPI, publishWhatsAppMenuAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { companyUrl } from '../../../utils/mhNav';
import styles from './WhatsAppMenuBuilder.module.scss';

const ACTIONS = ['book_flow', 'my_meetings', 'availability', 'mom_list', 'help'];

export default function WhatsAppMenuBuilder() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pubOpen, setPubOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWhatsAppMenuConfigAPI();
      setCfg(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateQr = (idx, field, val) => {
    const quickReplies = [...(cfg.quickReplies || [])];
    quickReplies[idx] = { ...quickReplies[idx], [field]: val };
    setCfg({ ...cfg, quickReplies });
  };

  const addQr = () => {
    if ((cfg.quickReplies || []).length >= 3) {
      toast.warning('WhatsApp limits to 3 quick reply buttons per message');
      return;
    }
    setCfg({
      ...cfg,
      quickReplies: [...(cfg.quickReplies || []), { id: `q-${Date.now()}`, label: 'New', action: 'help' }],
    });
  };

  if (loading && !cfg) {
    return (
      <div>
        <PageHeader title="WhatsApp menu" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Channels', path: companyUrl('channels') }, { label: 'WhatsApp' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !cfg) {
    return (
      <div>
        <PageHeader title="WhatsApp menu" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Channels', path: companyUrl('channels') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="WhatsApp menu builder"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Channels', path: companyUrl('channels') },
          { label: 'WhatsApp' },
        ]}
        secondaryActions={[
          { label: 'Add quick reply', onClick: addQr, variant: 'outline-secondary' },
          { label: 'Preview', onClick: () => toast.info('See phone frame →'), variant: 'outline-secondary' },
          { label: 'Publish', onClick: () => setPubOpen(true), variant: 'primary' },
        ]}
      />

      <Row className="g-3">
        <Col lg={5}>
          <Card>
            <Card.Body>
              <h2 className="h6">Menu tree</h2>
              <Form.Label>Initial menu type</Form.Label>
              <div className="mb-3">
                <Form.Check
                  inline
                  type="radio"
                  label="Quick reply buttons"
                  checked={cfg.menuType === 'quick_reply'}
                  onChange={() => setCfg({ ...cfg, menuType: 'quick_reply' })}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="List menu"
                  checked={cfg.menuType === 'list'}
                  onChange={() => setCfg({ ...cfg, menuType: 'list' })}
                />
              </div>
              <h3 className="h6">Quick replies</h3>
              {(cfg.quickReplies || []).map((q, i) => (
                <div key={q.id} className="border rounded p-2 mb-2">
                  <Form.Control size="sm" className="mb-1" value={q.label} onChange={(e) => updateQr(i, 'label', e.target.value)} />
                  <Form.Select size="sm" value={q.action} onChange={(e) => updateQr(i, 'action', e.target.value)}>
                    {ACTIONS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Label className="small mt-2 mb-0">Follow-up</Form.Label>
                  <Form.Select
                    size="sm"
                    value={cfg.followUp?.[q.action] || 'Book Meeting flow'}
                    onChange={(e) =>
                      setCfg({
                        ...cfg,
                        followUp: { ...cfg.followUp, [q.action]: e.target.value },
                      })
                    }
                  >
                    <option>Book Meeting flow</option>
                    <option>Show My Meetings</option>
                    <option>Check Availability</option>
                    <option>MOM list</option>
                  </Form.Select>
                </div>
              ))}
              <Button variant="link" size="sm" className="p-0" onClick={addQr}>
                + Add quick reply
              </Button>
              {(cfg.quickReplies || []).length > 3 && <p className="text-warning small">WhatsApp limits to 3 quick reply buttons</p>}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7}>
          <div className={styles.phoneFrame}>
            <div className={styles.bubble}>
              <strong>MeetingHub</strong>
              <div className="mt-1 small">How can I help you today?</div>
            </div>
            {cfg.menuType === 'quick_reply' ? (
              <div className={styles.qrRow}>
                {(cfg.quickReplies || []).map((q) => (
                  <div key={q.id} className={styles.qrBtn}>
                    {q.label}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 rounded overflow-hidden">
                {['Book meeting', 'My meetings', 'Help'].map((t) => (
                  <div key={t} className={styles.listItem}>
                    {t}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={pubOpen}
        onClose={() => setPubOpen(false)}
        onConfirm={async () => {
          try {
            await publishWhatsAppMenuAPI(cfg);
            toast.success('Published');
            setPubOpen(false);
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Publish WhatsApp menu?"
        message="Updates the live flow (mock)."
        confirmLabel="Publish"
      />
    </div>
  );
}
